import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import EquipmentPageService from './EquipmentPage.service';
import './EquipmentPage.css';

const EQUIPMENT_CATEGORY_MACHINE = 10001;
const EQUIPMENT_CATEGORY_TOOLS = 10002;

function EquipmentPage() {
  const { userData, onLogout } = useOutletContext();
  const [activeRegistry, setActiveRegistry] = useState(null); // 'machines' | 'tools' | null
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formName, setFormName] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const isMachines = activeRegistry === 'machines';
  const isTools = activeRegistry === 'tools';

  const loadList = () => {
    if (!activeRegistry) return;
    setLoading(true);
    setError(null);
    const promise = isMachines
      ? EquipmentPageService.getMachines(EQUIPMENT_CATEGORY_MACHINE)
      : EquipmentPageService.getToolsAndTackles(EQUIPMENT_CATEGORY_TOOLS);
    promise
      .then((data) => {
        if (isMachines) {
          setList(data?.machines || []);
        } else {
          setList(data?.toolsTackles || []);
        }
      })
      .catch((err) => {
        setError(err.message || 'Failed to load list');
        setList([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadList();
  }, [activeRegistry]);

  const openRegistry = (type) => {
    setActiveRegistry(type);
    setShowForm(false);
    setEditingItem(null);
    setFormName('');
    setError(null);
  };

  const closePanel = () => {
    setActiveRegistry(null);
    setShowForm(false);
    setEditingItem(null);
    setFormName('');
    setError(null);
  };

  const handleAddNew = () => {
    setEditingItem(null);
    setFormName('');
    setShowForm(true);
  };

  const handleEdit = (item) => {
    if (isMachines) {
      setEditingItem(item);
      setFormName(item.machineName || '');
    } else {
      setEditingItem(item);
      setFormName(item.toolsTacklesName || '');
    }
    setShowForm(true);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingItem(null);
    setFormName('');
  };

  const handleSave = () => {
    const name = formName.trim();
    if (!name) return;
    setSaving(true);
    setError(null);
    if (isMachines) {
      const promise = editingItem
        ? EquipmentPageService.editMachine(editingItem.machineId, name, EQUIPMENT_CATEGORY_MACHINE)
        : EquipmentPageService.addMachine(name, EQUIPMENT_CATEGORY_MACHINE);
      promise
        .then(() => {
          loadList();
          setShowForm(false);
          setEditingItem(null);
          setFormName('');
        })
        .catch((err) => setError(err.message || 'Failed to save'))
        .finally(() => setSaving(false));
    } else {
      const promise = editingItem
        ? EquipmentPageService.editToolsAndTackles(editingItem.toolsTacklesId, name, EQUIPMENT_CATEGORY_TOOLS)
        : EquipmentPageService.addToolsAndTackles(name, EQUIPMENT_CATEGORY_TOOLS);
      promise
        .then(() => {
          loadList();
          setShowForm(false);
          setEditingItem(null);
          setFormName('');
        })
        .catch((err) => setError(err.message || 'Failed to save'))
        .finally(() => setSaving(false));
    }
  };

  const handleDelete = (item) => {
    const id = isMachines ? item.machineId : item.toolsTacklesId;
    setDeletingId(id);
    setError(null);
    const promise = isMachines
      ? EquipmentPageService.deleteMachine(id)
      : EquipmentPageService.deleteToolsAndTackles(id);
    promise
      .then(() => loadList())
      .catch((err) => setError(err.message || 'Failed to delete'))
      .finally(() => setDeletingId(null));
  };

  const panelTitle = isMachines ? 'Machine Registry' : 'Tools & Tackles Registry';
  const itemNameLabel = isMachines ? 'Machine name' : 'Tools & Tackles name';
  const addButtonLabel = isMachines ? 'Add New Machine' : 'Add New Tools & Tackles';
  const emptyMessage = isMachines
    ? 'No machines yet. Add your first machine.'
    : 'No tools & tackles yet. Add your first item.';

  return (
    <div className="equipment-page">
      <div className="equipment-content">
        <div className="main-content-wrapper">
          {activeRegistry ? (
            <div className="registry-manage-panel">
              <header className="registry-panel-header">
                <h1 className="registry-panel-title">{panelTitle}</h1>
                <button
                  type="button"
                  className="registry-panel-back"
                  onClick={closePanel}
                  aria-label="Back to equipment"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Back to Equipment
                </button>
              </header>

              <div className="registry-panel-body">
                {error && (
                  <p className="registry-error" role="alert">{error}</p>
                )}

                {!showForm ? (
                  <>
                    <div className="registry-section-header">
                      <h2 className="registry-section-title">{panelTitle}</h2>
                      <button type="button" className="registry-add-button" onClick={handleAddNew}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10 4V16M4 10H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        {addButtonLabel}
                      </button>
                    </div>

                    {loading ? (
                      <p className="registry-loading">Loading...</p>
                    ) : list.length === 0 ? (
                      <div className="registry-empty">
                        <p>{emptyMessage}</p>
                        <button type="button" className="registry-empty-button" onClick={handleAddNew}>
                          {addButtonLabel}
                        </button>
                      </div>
                    ) : (
                      <ul className="registry-list" role="list">
                        {list.map((item) => {
                          const id = isMachines ? item.machineId : item.toolsTacklesId;
                          const name = isMachines ? item.machineName : item.toolsTacklesName;
                          return (
                            <li key={id} className="registry-item">
                              <span className="registry-item-check" aria-hidden>
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M16.6667 5L7.50004 14.1667L3.33337 10" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </span>
                              <div className="registry-item-content">
                                <span className="registry-item-name">{name}</span>
                              </div>
                              <div className="registry-item-actions">
                                <button
                                  type="button"
                                  className="registry-edit-btn"
                                  onClick={() => handleEdit(item)}
                                  disabled={saving}
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  className="registry-delete-btn"
                                  onClick={() => handleDelete(item)}
                                  disabled={saving || deletingId === id}
                                >
                                  {deletingId === id ? 'Deleting...' : 'Remove'}
                                </button>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </>
                ) : (
                  <div className="registry-form">
                    <h3 className="registry-form-title">
                      {editingItem ? `Edit ${itemNameLabel}` : `Add ${itemNameLabel}`}
                    </h3>
                    <div className="registry-form-group">
                      <label htmlFor="registry-name">{itemNameLabel} <span className="required">*</span></label>
                      <input
                        id="registry-name"
                        type="text"
                        value={formName}
                        onChange={(e) => setFormName(e.target.value)}
                        placeholder={isMachines ? 'e.g. Air Compressor' : 'e.g. Shackles'}
                      />
                    </div>
                    <div className="registry-form-actions">
                      <button type="button" className="registry-cancel-btn" onClick={handleCancelForm} disabled={saving}>
                        Cancel
                      </button>
                      <button type="button" className="registry-save-btn" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving...' : (editingItem ? 'Update' : 'Save')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="equipment-header">
                <div>
                  <h1 className="equipment-title">Equipment Registry</h1>
                  <p className="equipment-subtitle">
                    Keep your machines and tools &amp; tackles registry organised.
                  </p>
                </div>
              </div>

              <div className="equipment-grid">
                <div className="equipment-card">
                  <div className="card-icon-wrapper">
                    <div className="card-icon equipment-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4 6H20M4 12H20M4 18H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M8 4V8M16 4V8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </div>
                  </div>
                  <h3 className="card-title">Machine Registry</h3>
                  <p className="card-subtitle">View and manage registered machines</p>
                  <button className="manage-button" onClick={() => openRegistry('machines')}>
                    Open registry
                  </button>
                </div>

                <div className="equipment-card">
                  <div className="card-icon-wrapper">
                    <div className="card-icon equipment-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                  <h3 className="card-title">Tools &amp; Tackles Registry</h3>
                  <p className="card-subtitle">View and manage all tools &amp; tackles</p>
                  <button className="manage-button" onClick={() => openRegistry('tools')}>
                    Open registry
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default EquipmentPage;
